import { Button, Col, ConfigProvider, Form, Row } from "antd";
import {
  CloseOutlined,
  CreditCardOutlined,
  LoadingOutlined,
  LockOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import usePost from "../hooks/usePost";
import useGet from "../hooks/useGet";
import { openErrorNotification } from "../utils/toast";
import CustomInput from "./inputWidgets/CustomInput";
import CustomSelect from "./inputWidgets/CustomSelect";
import CustomSwitch from "./inputWidgets/CustomSwitch";
import IntuitCardFields from "./IntuitCardFields";
import { offlineMethodOptions } from "../../utils/Common";
import {
  brandKeyFromCard,
  CardBrandIcon,
  formatCardBrandLabel,
  formatExpiresOn,
  getCardExpirationError,
  MAX_SAVED_CARDS,
  normalizeCardsResponse,
  parseCardExpiration,
} from "../utils/cardPaymentUtils";
import { formatIntuitError, tokenizeIntuitCard } from "../utils/intuitPayments";
import PaymentSuccessModal, {
  computeValidityDates,
} from "./PaymentSuccessModal";

/**
 * Mount success modal outside PaymentForm so it survives when the parent
 * "Make Payment" Modal closes (callers use destroyOnClose).
 */
function openDetachedPaymentSuccessModal({
  dealerEmail,
  paymentNo,
  amount,
  method,
  paidAt,
  validTill,
  renewalAt,
  onClose,
}) {
  const container = document.createElement("div");
  container.setAttribute("data-payment-success-root", "");
  document.body.appendChild(container);
  const root = createRoot(container);

  const handleClose = () => {
    try {
      onClose?.();
    } finally {
      root.unmount();
      container.remove();
    }
  };

  const colorPrimary =
    getComputedStyle(document.documentElement)
      .getPropertyValue("--text-color")
      .trim() || undefined;

  root.render(
    <ConfigProvider
      theme={{
        components: {
          Button: {
            colorPrimary,
            colorPrimaryHover: "white",
          },
        },
        token: { fontFamily: '"Poppins", sans-serif' },
      }}
    >
      <PaymentSuccessModal
        open
        onClose={handleClose}
        dealerEmail={dealerEmail}
        paymentNo={paymentNo}
        amount={amount}
        method={method}
        paidAt={paidAt}
        validTill={validTill}
        renewalAt={renewalAt}
      />
    </ConfigProvider>,
  );
}

function getApiBase() {
  const raw = process.env.REACT_APP_API_URL;
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s || s === "undefined") return "";
  return s.replace(/\/+$/, "");
}

const apiBase = getApiBase();

function apiUrl(path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  return apiBase ? `${apiBase}${p}` : p;
}

function formatCardMethodLabel(brand, last4) {
  const b = formatCardBrandLabel(brand);
  const l4 = last4 != null && last4 !== "" ? String(last4) : "";
  if (b && l4) return `${b} **** ${l4}`;
  if (l4) return `**** ${l4}`;
  if (b) return b;
  return "";
}

function extractPaymentPayload(postData) {
  if (!postData || typeof postData !== "object") return {};
  return postData.data?.data ?? postData.data ?? postData;
}

function resolvePaidAtDate(payment) {
  const raw =
    payment?.PaymentTime ??
    payment?.payment_time ??
    payment?.PaidAt ??
    payment?.paid_at ??
    null;
  if (raw == null || raw === "") return new Date();
  if (
    typeof raw === "number" ||
    (/^\d+$/.test(String(raw)) && String(raw).length <= 12)
  ) {
    const n = Number(raw);
    // Unix seconds vs ms
    return new Date(n < 1e12 ? n * 1000 : n);
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function resolvePaymentNo(payment, postData) {
  return (
    payment?.PaymentNo ??
    payment?.payment_no ??
    payment?.id ??
    postData?.PaymentNo ??
    postData?.data?.PaymentNo ??
    null
  );
}

const MyPaymentForm = ({
  amount,
  setPaymentModal,
  InvoiceNo,
  OrderName,
  refetch,
  showCardLayout,
  setShowCardLayout,
  onPaymentSuccess,
  beforePayment,
  DealerNo,
  /** When true (site/device), show Valid till / Renewal per billing rules */
  showValidityDates = false,
  /** 0 = Invoice, 1 = Transaction */
  billingType = null,
  /** 0 = Monthly, 1 = Lifetime, 2 = Yearly (if used) */
  licensePeriod = null,
  /** Optional override; otherwise profile / session email */
  dealerEmail: dealerEmailProp,
}) => {
  const [loading, setLoading] = useState(false);
  /** saved | new */
  const [cardTab, setCardTab] = useState("saved");
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [saveCardForFuture, setSaveCardForFuture] = useState(true);
  const [internalShowCardLayout, internalSetShowCardLayout] = useState(true);
  const [manualForm] = Form.useForm();
  const [cardForm] = Form.useForm();
  const cvvInputRef = useRef(null);
  const pendingMethodRef = useRef("Card");

  const isCardLayoutControlled =
    typeof showCardLayout === "boolean" &&
    typeof setShowCardLayout === "function";
  const showCardComingSoonMessage =
    String(process.env.REACT_APP_CARD_PAYMENT_COMING_SOON ?? "true")
      .trim()
      .toLowerCase() !== "false";

  const effectiveShowCardLayout = isCardLayoutControlled
    ? showCardLayout
    : internalShowCardLayout;
  const effectiveSetShowCardLayout = isCardLayoutControlled
    ? setShowCardLayout
    : internalSetShowCardLayout;

  const getNumericAmount = (value) => {
    const raw = value ?? "";
    const num = Number(String(raw).replace(/[^0-9.-]+/g, ""));
    return Number.isFinite(num) ? num : NaN;
  };

  const numericAmount = getNumericAmount(amount);
  const displayAmountWithCurrency = Number.isFinite(numericAmount)
    ? `$${numericAmount.toFixed(2)}`
    : amount;
  const dealerNoStr =
    DealerNo != null && DealerNo !== "" ? String(DealerNo) : "";
  const cardsUrl = useMemo(() => {
    if (!dealerNoStr || !effectiveShowCardLayout || showCardComingSoonMessage) {
      return null;
    }
    return apiUrl(`/api/dealers/${dealerNoStr}/cards/`);
  }, [dealerNoStr, effectiveShowCardLayout, showCardComingSoonMessage]);

  const {
    data: cardsData,
    loading: cardsLoading,
    error: cardsError,
  } = useGet(cardsUrl);

  const profileUrl = useMemo(() => {
    if (dealerEmailProp) return null;
    return apiUrl("/api/accounts/profile/");
  }, [dealerEmailProp]);

  const { data: profileData } = useGet(profileUrl);

  const resolvedDealerEmail = useMemo(() => {
    if (dealerEmailProp) return String(dealerEmailProp);
    try {
      const ud = JSON.parse(sessionStorage.getItem("userData") || "null");
      const fromSession = ud?.email || ud?.Email || "";
      if (fromSession) return String(fromSession);
    } catch {
      /* ignore */
    }
    const fromProfile =
      profileData?.data?.Email ??
      profileData?.Email ??
      profileData?.data?.email ??
      "";
    return fromProfile ? String(fromProfile) : "";
  }, [dealerEmailProp, profileData]);

  const cards = useMemo(() => normalizeCardsResponse(cardsData), [cardsData]);

  const hasSavedCards = !cardsLoading && cards.length > 0;
  const showCardTabs = hasSavedCards;
  const activeCardTab = showCardTabs ? cardTab : "new";
  const canSaveNewCard = cards.length < MAX_SAVED_CARDS;

  useEffect(() => {
    if (!canSaveNewCard) {
      setSaveCardForFuture(false);
      cardForm.setFieldsValue({ SaveCardForFuture: false });
    }
  }, [canSaveNewCard, cardForm]);

  useEffect(() => {
    if (cardsLoading) return;
    if (!cards.length) {
      setSelectedCardId(null);
      setCardTab("new");
      return;
    }
    setCardTab((prev) => (prev === "new" ? prev : "saved"));
    setSelectedCardId((prev) => {
      if (
        prev != null &&
        cards.some(
          (c) => String(c.PaymentSourceID || c.DealerCardNo) === String(prev),
        )
      ) {
        return prev;
      }
      const def =
        cards.find((c) => c.IsDefault) ||
        cards.find((c) => c.PaymentSourceID || c.SquareCardID) ||
        cards[0];
      return def?.PaymentSourceID || def?.DealerCardNo || null;
    });
  }, [cards, cardsLoading]);

  const selectedCard = useMemo(
    () =>
      cards.find(
        (c) =>
          String(c.PaymentSourceID || c.DealerCardNo) ===
          String(selectedCardId),
      ),
    [cards, selectedCardId],
  );

  const selectedCardSourceId =
    selectedCard?.PaymentSourceID || selectedCard?.SquareCardID || null;

  const postUrl = `${process.env.REACT_APP_API_URL}/api/payments/`;
  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);

  useEffect(() => {
    refetch?.();
  }, [postData, postLoading]);

  const submitPayment = async (payload) => {
    const okToPay = beforePayment ? await beforePayment() : true;
    if (!okToPay) {
      setLoading(false);
      return;
    }
    postAction(payload);
  };

  const handlePayNewCard = async () => {
    try {
      const values = await cardForm.validateFields([
        "CardNumber",
        "ExpDate",
        "Cvc",
        "CardHolderName",
      ]);
      const parsedExp = parseCardExpiration(values.ExpDate);
      if (!parsedExp) {
        cardForm.setFields([
          { name: "ExpDate", errors: ["Enter expiration as MM/YY."] },
        ]);
        return;
      }
      const expError = getCardExpirationError(parsedExp.month, parsedExp.year);
      if (expError) {
        cardForm.setFields([{ name: "ExpDate", errors: [expError] }]);
        openErrorNotification(expError);
        return;
      }

      setLoading(true);
      const cardNumber = String(values.CardNumber || "").replace(/\D/g, "");
      const cvc = String(values.Cvc || "").replace(/\D/g, "");
      const name = String(values.CardHolderName || "").trim();
      const postalCode = String(
        cardForm.getFieldValue("PostalCode") || "",
      ).trim();
      const saveCard = canSaveNewCard && saveCardForFuture;

      const token = await tokenizeIntuitCard({
        number: cardNumber,
        expMonth: parsedExp.month,
        expYear: parsedExp.year,
        cvc,
        name,
        postalCode: postalCode || undefined,
        isDefault: saveCard,
      });

      pendingMethodRef.current =
        formatCardMethodLabel(null, cardNumber.slice(-4)) || "Card";
      const safeAmount = getNumericAmount(amount);
      await submitPayment({
        dealer_id: DealerNo,
        source_id: token,
        amount: safeAmount,
        save_card: saveCard,
      });
    } catch (e) {
      setLoading(false);
      if (e?.errorFields) return;
      openErrorNotification(
        formatIntuitError(e?.response?.data, "") ||
          e?.message ||
          "Could not process payment.",
      );
    }
  };

  const handlePaySavedCard = async () => {
    try {
      if (!selectedCardSourceId) {
        openErrorNotification("Select a saved card to pay.");
        return;
      }
      setLoading(true);
      pendingMethodRef.current =
        formatCardMethodLabel(selectedCard.CardBrand, selectedCard.CardLast4) ||
        "Saved card";
      const safeAmount = getNumericAmount(amount);
      await submitPayment({
        dealer_id: DealerNo,
        amount: safeAmount,
        dealer_card_no: selectedCardSourceId,
      });
    } catch (e) {
      setLoading(false);
    }
  };

  const handlePayWithoutNewCard = async () => {
    try {
      const values = await manualForm.validateFields();
      const offlineMethod = Number(values.offline_method);
      const offlineRef =
        values.offline_method_reference != null
          ? String(values.offline_method_reference).trim()
          : "";
      const methodLabel =
        offlineMethodOptions.find((o) => o.value === offlineMethod)?.label ||
        "Manual";
      const safeAmount = getNumericAmount(amount);
      setLoading(true);
      pendingMethodRef.current = methodLabel;
      await submitPayment({
        dealer_id: DealerNo,
        amount: safeAmount,
        payment_mode: "MANUAL",
        offline_method: offlineMethod,
        ...(offlineRef ? { offline_method_reference: offlineRef } : {}),
      });
    } catch (e) {
      setLoading(false);
    }
  };

  const handlePayClick = () => {
    if (!effectiveShowCardLayout) {
      handlePayWithoutNewCard();
      return;
    }
    if (showCardComingSoonMessage) return;

    if (activeCardTab === "saved") {
      handlePaySavedCard();
      return;
    }

    handlePayNewCard();
  };

  useEffect(() => {
    if (postError) {
      setLoading(false);
      openErrorNotification(
        formatIntuitError(postError?.response?.data, "") ||
          postError?.response?.data?.message ||
          "Something went wrong",
      );
    } else if (!postLoading && !postError && postData) {
      let cancelled = false;
      (async () => {
        try {
          const afterClose = await onPaymentSuccess?.(postData);
          if (cancelled) return;

          const payment = extractPaymentPayload(postData);
          const paidAt = resolvePaidAtDate(payment);
          const paymentNo = resolvePaymentNo(payment, postData);
          const responseLast4 = payment?.CardLast4 ?? payment?.card_last4;
          let method = pendingMethodRef.current || "Card";
          if (
            (!method || method === "Card" || method === "Saved card") &&
            responseLast4
          ) {
            method = formatCardMethodLabel(null, responseLast4) || method;
          }
          const amountValue =
            payment?.Amount ?? payment?.amount ?? numericAmount ?? amount;

          const { validTill, renewalAt } = computeValidityDates({
            showValidityDates,
            billingType,
            licensePeriod,
            paidAt,
          });

          // Detach success modal first so it survives parent destroyOnClose,
          // then close Make Payment UI immediately (avoids empty modal chrome).
          openDetachedPaymentSuccessModal({
            dealerEmail: resolvedDealerEmail,
            paymentNo,
            amount: amountValue,
            method,
            paidAt,
            validTill,
            renewalAt,
            onClose: () => {
              if (typeof afterClose === "function") {
                try {
                  afterClose();
                } catch {
                  /* ignore navigation errors */
                }
              }
            },
          });
          setPaymentModal?.(false);
        } catch (e) {
          if (cancelled) return;
          openErrorNotification(
            e?.response?.data?.message || e?.message || "Something went wrong",
          );
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }
  }, [postError, postData, postLoading]);

  const paymentHelperText = effectiveShowCardLayout
    ? `Enter your card details to pay ${displayAmountWithCurrency}.`
    : `Complete payment of ${displayAmountWithCurrency} manually.`;
  const cardComingSoonMessage =
    "We are preparing a card payment system.\nPlease contact 857-346-0130 for Prepurchased License.";

  return (
    <>
      <style>{`
        .payment-form-fields .ant-form-item {
          margin-bottom: 0 !important;
        }
      `}</style>

      {!effectiveShowCardLayout && (
        <div className="px-2 mb-3">
          <p className="text-sm font-[Poppins] mb-3">{paymentHelperText}</p>
          <Form
            form={manualForm}
            layout="vertical"
            autoComplete="off"
            className="payment-form-fields"
            initialValues={{ offline_method: undefined }}
          >
            <Row gutter={[16, 8]}>
              <Col span={24}>
                <CustomSelect
                  label="Offline Method"
                  name="offline_method"
                  options={offlineMethodOptions}
                  placeholder="Please select"
                  required
                  allowClear={false}
                />
              </Col>
              <Col span={24}>
                <CustomInput
                  label="Offline Method Reference"
                  name="offline_method_reference"
                />
              </Col>
            </Row>
          </Form>
        </div>
      )}
      {effectiveShowCardLayout && !showCardComingSoonMessage && (
        <p className="text-sm font-[Poppins] px-2 mb-2">{paymentHelperText}</p>
      )}
      {effectiveShowCardLayout && (
        <>
          {showCardComingSoonMessage ? (
            <div className="px-2">
              <div className="w-full rounded-md border border-[#91d5ff] bg-[#e6f7ff] px-4 py-3 text-center">
                <p className="text-sm font-[Poppins] text-[#003a8c] m-0 whitespace-pre-line">
                  {cardComingSoonMessage}
                </p>
              </div>
            </div>
          ) : (
            <div className="px-2">
              {cardsLoading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-[#64748b]">
                  <LoadingOutlined className="text-[16px]" />
                  <span className="text-sm font-[Poppins]">
                    Loading payment methods…
                  </span>
                </div>
              ) : cardsError ? (
                <p className="text-sm font-[Poppins] m-0 py-4 text-center text-red-500">
                  Could not load saved cards.
                </p>
              ) : (
                <>
                  {showCardTabs ? (
                    <div className="grid grid-cols-2 gap-1 p-0.5 mb-1.5 rounded-md bg-[#f1f5f9]">
                      <button
                        type="button"
                        onClick={() => setCardTab("saved")}
                        className={`inline-flex items-center justify-center gap-1.5 rounded h-[30px] text-[12px] font-[Poppins] leading-none border-0 cursor-pointer ${
                          activeCardTab === "saved"
                            ? "bg-white text-black shadow-sm"
                            : "bg-transparent text-[#64748b]"
                        }`}
                      >
                        <CreditCardOutlined className="text-[12px] leading-none flex items-center" />
                        <span className="leading-none">Saved card</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setCardTab("new")}
                        className={`inline-flex items-center justify-center gap-1.5 rounded h-[30px] text-[12px] font-[Poppins] leading-none border-0 cursor-pointer ${
                          activeCardTab === "new"
                            ? "bg-white text-black shadow-sm"
                            : "bg-transparent text-[#64748b]"
                        }`}
                      >
                        <PlusOutlined className="text-[12px] leading-none flex items-center" />
                        <span className="leading-none">New card</span>
                      </button>
                    </div>
                  ) : null}

                  {activeCardTab === "saved" && showCardTabs ? (
                    <div className="flex flex-col gap-2.5 mb-2 max-h-[min(320px,48vh)] sm:max-h-none overflow-y-auto sm:overflow-visible">
                      {cards.map((card) => {
                        const cardKey = String(
                          card.PaymentSourceID || card.DealerCardNo,
                        );
                        const selected = cardKey === String(selectedCardId);
                        const brandLabel = formatCardBrandLabel(card.CardBrand);
                        const masked =
                          card.CardNumberMasked ||
                          (card.CardLast4
                            ? `xxxxxxxxxxxx${card.CardLast4}`
                            : "");
                        const titleParts = [
                          brandLabel && brandLabel !== "Card"
                            ? brandLabel
                            : null,
                          masked || null,
                        ].filter(Boolean);
                        const title =
                          titleParts.length > 0
                            ? titleParts.join(" ")
                            : "Saved card";
                        const exp = formatExpiresOn(card);
                        return (
                          <button
                            key={cardKey}
                            type="button"
                            onClick={() => setSelectedCardId(cardKey)}
                            className={`w-full text-left rounded-md border px-3 py-2.5 flex items-center gap-3 cursor-pointer transition-colors ${
                              selected
                                ? "border-[var(--text-color,#1677ff)] bg-[var(--text-color-op-10,#e6f4ff)]"
                                : "border-[#e5e7eb] bg-white hover:border-[#cbd5e1]"
                            }`}
                          >
                            <CardBrandIcon
                              brandKey={brandKeyFromCard(card)}
                              className="shrink-0"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="m-0 text-[13px] font-medium font-[Poppins] text-black leading-snug">
                                {title}
                              </p>
                              <p className="m-0 mt-0.5 text-[11px] text-[#64748b] font-[Poppins] leading-snug">
                                {[card.CardHolderName || null, exp || null]
                                  .filter(Boolean)
                                  .join(" · ") || "Saved payment method"}
                              </p>
                            </div>
                            <span
                              className={`shrink-0 h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                                selected
                                  ? "border-[var(--text-color,#1677ff)]"
                                  : "border-[#cbd5e1]"
                              }`}
                              aria-hidden
                            >
                              {selected ? (
                                <span className="h-2 w-2 rounded-full bg-[var(--text-color,#1677ff)]" />
                              ) : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="mb-1">
                      <Form
                        form={cardForm}
                        layout="vertical"
                        autoComplete="off"
                        validateTrigger="onChange"
                        className="payment-form-fields"
                        initialValues={{ SaveCardForFuture: true }}
                      >
                        <IntuitCardFields
                          form={cardForm}
                          cvvInputRef={cvvInputRef}
                        >
                          {canSaveNewCard ? (
                            <Col span={24}>
                              <CustomSwitch
                                label="Save card for future use"
                                name="SaveCardForFuture"
                                defaultChecked={true}
                                onChange={setSaveCardForFuture}
                              />
                            </Col>
                          ) : null}
                        </IntuitCardFields>
                      </Form>
                    </div>
                  )}
                </>
              )}

              <div className="flex items-center justify-center gap-1.5 text-gray-500 text-[12px] mt-3 mb-1">
                <LockOutlined className="text-[12px]" />
                <span>Your payment is securely processed</span>
              </div>
            </div>
          )}
        </>
      )}

      <div className="flex justify-center space-x-2 mt-1.5">
        <Button
          type="primary"
          className="min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
          loading={loading}
          disabled={
            cardsLoading ||
            (effectiveShowCardLayout && showCardComingSoonMessage) ||
            (effectiveShowCardLayout &&
              !showCardComingSoonMessage &&
              activeCardTab === "saved" &&
              !selectedCardSourceId)
          }
          onClick={handlePayClick}
        >
          <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
          <span>Pay</span>
        </Button>
        <Button
          className="reset-button"
          onClick={() => setPaymentModal(false)}
          disabled={loading}
        >
          <CloseOutlined />
          <span>Cancel</span>
        </Button>
      </div>
    </>
  );
};

export default MyPaymentForm;
